import type { AvailableUnit, CatalogueDesign, ItemVariantAxis } from '../api/masters';
import { formatDate } from '../format/dates';
import { humanizeEnum } from '../format/text';
import { UnitConditionValues } from '../generated/enums';
import { matchesGarmentCode } from '../units/garmentCode';
import { normalizeReference } from '../units/referenceNumber';
import { rateOf, type TagRateLine } from '../print/hangTag';
import type { StatusTone } from '../vocabulary';
import { priceGarment, type SlabCharge } from './pricing';

/**
 * What a design card and a design page say about one design's garments over a booking's dates —
 * once, for the website's rail and the phone's catalogue alike.
 *
 * <p>
 * <strong>Size and look are told apart by the garment-code mark, never by name.</strong> An axis
 * that goes into garment codes (size; fit where a shop marks it) is what a customer picks between
 * on the design page; every other axis (colour, fabric) is the look a photograph shows. That is the
 * same mark that decides which sibling's photograph a bare garment borrows, so the page and the
 * pictures agree about what "the same look" means.
 * </p>
 *
 * <p>
 * <strong>Booking by size still reserves one garment.</strong> {@link pickGarment} is the rule
 * that turns "maroon, size 40" into a serial: free, best condition, then the one rented least so
 * wear spreads across the stock, then the code so two screens choose the same one.
 * </p>
 */

/** One value of an axis, as a chip. */
export interface AxisValueChoice {
  valueId: number;
  label: string;
  colourHex: string | null;
}

/** The value ids of a garment's axes on one side of the size/look split, as a stable key. */
function keyOf(axes: readonly ItemVariantAxis[], sizes: boolean): string {
  return axes
    .filter((axis) => axis.inGarmentCode === sizes)
    .map((axis) => `${axis.attributeId}:${axis.valueId}`)
    .sort()
    .join('/');
}

/** Which look a garment is — every axis except the garment-code ones. Empty when it has none. */
export const lookKey = (unit: Pick<AvailableUnit, 'axes'>): string => keyOf(unit.axes, false);

/** Which size a garment is — the garment-code axes. Empty for a one-size design. */
export const sizeKey = (unit: Pick<AvailableUnit, 'axes'>): string => keyOf(unit.axes, true);

/** Values in the shop's order: its arrangement, then the number (8 before 10), then the name. */
function compareValues(a: ItemVariantAxis, b: ItemVariantAxis): number {
  return (
    a.valueSortOrder - b.valueSortOrder ||
    (a.valueNumeric ?? Number.MAX_SAFE_INTEGER) - (b.valueNumeric ?? Number.MAX_SAFE_INTEGER) ||
    a.valueName.localeCompare(b.valueName)
  );
}

/** A garment's axes on one side of the split, in attribute then value order. */
function sideOf(unit: Pick<AvailableUnit, 'axes'>, sizes: boolean): ItemVariantAxis[] {
  return unit.axes
    .filter((axis) => axis.inGarmentCode === sizes)
    .sort((a, b) => a.attributeId - b.attributeId);
}

/** "40", or "40 · Slim" where fit is marked too. */
export function sizeLabel(unit: Pick<AvailableUnit, 'axes'>): string {
  return sideOf(unit, true)
    .map((axis) => axis.valueName)
    .join(' · ');
}

/** "Maroon", or "Maroon · Raw silk". */
export function lookLabel(unit: Pick<AvailableUnit, 'axes'>): string {
  return sideOf(unit, false)
    .map((axis) => axis.valueName)
    .join(' · ');
}

const conditionRank = (unit: Pick<AvailableUnit, 'condition'>) =>
  UnitConditionValues.indexOf(unit.condition);

/**
 * The garment set aside when somebody picks a look and a size rather than a serial: free on the
 * dates, then the best condition, then the one rented least, then the lowest code. Null when no
 * garment of that look and size is free. An empty key matches anything on that side, so a design
 * with no look axis, or a one-size safa, still resolves.
 */
export function pickGarment<T extends AvailableUnit>(
  units: readonly T[],
  wanted: { look?: string | undefined; size?: string | undefined },
): T | null {
  const candidates = units.filter(
    (unit) =>
      unit.free &&
      (wanted.look === undefined || lookKey(unit) === wanted.look) &&
      (wanted.size === undefined || sizeKey(unit) === wanted.size),
  );

  candidates.sort(
    (a, b) =>
      conditionRank(b) - conditionRank(a) ||
      a.rentalCount - b.rentalCount ||
      a.serialCode.localeCompare(b.serialCode, 'en', { numeric: true }),
  );

  return candidates[0] ?? null;
}

/** The earliest day any of these garments comes back, or null when none is out. */
function earliestBack(units: readonly AvailableUnit[]): string | null {
  const dates = units
    .map((unit) => unit.nextFreeFrom)
    .filter((date): date is string => date !== null)
    .sort();

  return dates[0] ?? null;
}

/** The cheapest of these garments for the charged days, or null when nothing prices any of them. */
function cheapest(units: readonly AvailableUnit[], days: number): number | null {
  const prices = units
    .map((unit) => priceGarment(unit.rateCard, days)?.amount)
    .filter((amount): amount is number => amount !== undefined);

  return prices.length === 0 ? null : Math.min(...prices);
}

/** One choice on the design page — a look, or a size within a look. */
export interface DesignOption {
  key: string;
  label: string;
  colourHex: string | null;
  freeCount: number;
  total: number;
  /** When the first taken garment of this choice comes back, while none of it is free. */
  backOn: string | null;
  /** The cheapest free garment's price for the days; the cheapest of all while none is free. */
  price: number | null;
}

function optionOf(
  key: string,
  label: string,
  colourHex: string | null,
  units: readonly AvailableUnit[],
  days: number,
): DesignOption {
  const free = units.filter((unit) => unit.free);

  return {
    key,
    label,
    colourHex,
    freeCount: free.length,
    total: units.length,
    backOn: free.length === 0 ? earliestBack(units) : null,
    price: cheapest(free.length > 0 ? free : units, days),
  };
}

/** Groups garments by a key, keeping the first garment seen as the group's representative. */
function groupBy(
  units: readonly AvailableUnit[],
  key: (unit: AvailableUnit) => string,
): Map<string, AvailableUnit[]> {
  const groups = new Map<string, AvailableUnit[]>();

  for (const unit of units) {
    const k = key(unit);
    groups.set(k, [...(groups.get(k) ?? []), unit]);
  }

  return groups;
}

/** The axis values a group sorts on — its first garment's, on one side of the split. */
function sortAxes(units: readonly AvailableUnit[], sizes: boolean): ItemVariantAxis[] {
  return units[0] ? sideOf(units[0], sizes) : [];
}

function compareGroups(a: ItemVariantAxis[], b: ItemVariantAxis[]): number {
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    const left = a[i];
    const right = b[i];

    if (left && right) {
      const order = compareValues(left, right);

      if (order !== 0) {
        return order;
      }
    }
  }

  return a.length - b.length;
}

/**
 * The looks a design comes in — the colour row on a design page — each with what is free. A design
 * with no look axis has one, with an empty key and label, which a screen simply does not draw.
 */
export function lookOptions(units: readonly AvailableUnit[], days: number): DesignOption[] {
  return [...groupBy(units, lookKey).entries()]
    .sort(([, a], [, b]) => compareGroups(sortAxes(a, false), sortAxes(b, false)))
    .map(([key, group]) => {
      const first = group[0];
      const colour = first?.axes.find((axis) => !axis.inGarmentCode && axis.colourHex);

      return optionOf(key, first ? lookLabel(first) : '', colour?.colourHex ?? null, group, days);
    });
}

/**
 * The sizes of one look — the chips on a design page — each with what is free, when a taken size
 * comes back, and its price for the days. Every look's sizes when `look` is undefined.
 */
export function sizeOptions(
  units: readonly AvailableUnit[],
  look: string | undefined,
  days: number,
): DesignOption[] {
  const ofLook = look === undefined ? units : units.filter((unit) => lookKey(unit) === look);

  return [...groupBy(ofLook, sizeKey).entries()]
    .sort(([, a], [, b]) => compareGroups(sortAxes(a, true), sortAxes(b, true)))
    .map(([key, group]) => optionOf(key, group[0] ? sizeLabel(group[0]) : '', null, group, days));
}

/** What a design card shows. */
export interface DesignCard {
  itemId: number;
  name: string;
  code: string;
  imageUrl: string | null;
  freeCount: number;
  total: number;
  /** "38–44", "40", or empty for a design with no size axis. */
  sizes: string;
  /** "Maroon, Gold", or "Navy, Black · Slim fit, Regular fit" — each look axis once. */
  looks: string;
  /** The cheapest free garment for the days, or the cheapest of all while none is free. */
  price: number | null;
  /** True when its free garments do not all cost the same — the card says "from". */
  priceVaries: boolean;
  /** When the first garment comes back, while none is free. */
  backOn: string | null;
}

/**
 * "38–44" for a run of three or more numbered sizes (or more than four of anything), otherwise the
 * list — "38, 44" must not suggest a 40 and a 42 the shop does not stock.
 */
function sizeRange(options: readonly DesignOption[]): string {
  const labels = options.map((option) => option.label).filter((label) => label !== '');

  if (labels.length <= 1) {
    return labels[0] ?? '';
  }

  return labels.length > 4 || (labels.length >= 3 && labels.every((label) => /^\d+$/.test(label)))
    ? `${labels[0]}–${labels[labels.length - 1]}`
    : labels.join(', ');
}

/**
 * "Navy, Black · Slim fit, Regular fit" — each look axis once, its values in the shop's order. Not
 * the looks themselves: three looks over two axes, listed whole, read "Navy · Slim fit · Navy ·
 * Regular fit · Black · Slim fit", naming one colour twice on a line with room for one row.
 */
function lookSummary(units: readonly AvailableUnit[]): string {
  const axes = new Map<number, Map<number, ItemVariantAxis>>();

  for (const unit of units) {
    for (const axis of sideOf(unit, false)) {
      const values = axes.get(axis.attributeId) ?? new Map<number, ItemVariantAxis>();
      values.set(axis.valueId, axis);
      axes.set(axis.attributeId, values);
    }
  }

  return [...axes.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, values]) =>
      [...values.values()]
        .sort(compareValues)
        .map((axis) => axis.valueName)
        .join(', '),
    )
    .join(' · ');
}

export function designCard(design: CatalogueDesign, days: number): DesignCard {
  const free = design.units.filter((unit) => unit.free);
  const priced = (free.length > 0 ? free : design.units)
    .map((unit) => priceGarment(unit.rateCard, days)?.amount)
    .filter((amount): amount is number => amount !== undefined);

  return {
    itemId: design.itemId,
    name: design.itemName,
    code: design.itemCode,
    imageUrl: design.imageUrl,
    freeCount: free.length,
    total: design.units.length,
    sizes: sizeRange(sizeOptions(design.units, undefined, days)),
    looks: lookSummary(design.units),
    price: priced.length === 0 ? null : Math.min(...priced),
    priceVaries: new Set(priced).size > 1,
    backOn: free.length === 0 ? earliestBack(design.units) : null,
  };
}

/** An availability in words, with the tone it is drawn in. Never colour alone. */
export interface AvailabilityWords {
  tone: Extract<StatusTone, 'positive' | 'warning' | 'negative' | 'brand'>;
  label: string;
}

/**
 * The word on a card's photograph: "4 of 6 free" when it can go, "Back 24-12-2026" when none can.
 * Never another customer's name, since a counter screen is often turned to face the customer.
 */
export function cardAvailability(
  card: Pick<DesignCard, 'freeCount' | 'total' | 'backOn'>,
): AvailabilityWords {
  if (card.freeCount > 0) {
    return {
      tone: 'positive',
      label:
        card.freeCount === card.total
          ? `${card.freeCount} free`
          : `${card.freeCount} of ${card.total} free`,
    };
  }

  return card.backOn
    ? { tone: 'warning', label: `Back ${formatDate(card.backOn)}` }
    : { tone: 'negative', label: 'None free' };
}

/** The line under a look or size chip: "3 free", "back 24-12-2026", or "none free". */
export function optionAvailability(option: Pick<DesignOption, 'freeCount' | 'backOn'>): string {
  if (option.freeCount > 0) {
    return `${option.freeCount} free`;
  }

  return option.backOn ? `back ${formatDate(option.backOn)}` : 'none free';
}

/**
 * One garment's state on the design page: already in the booking being taken, free, back on a day,
 * or taken with no day known.
 */
export function garmentAvailability(
  unit: Pick<AvailableUnit, 'free' | 'nextFreeFrom'>,
  inBooking = false,
): AvailabilityWords {
  if (inBooking) {
    return { tone: 'brand', label: 'In this booking' };
  }

  if (unit.free) {
    return { tone: 'positive', label: 'Free' };
  }

  return {
    tone: 'warning',
    label: unit.nextFreeFrom ? `Back ${formatDate(unit.nextFreeFrom)}` : 'Taken',
  };
}

/** "Excellent · rented 4 times", or "New · not rented yet". */
export function garmentWear(unit: Pick<AvailableUnit, 'condition' | 'rentalCount'>): string {
  const rented =
    unit.rentalCount === 0
      ? 'not rented yet'
      : `rented ${unit.rentalCount} time${unit.rentalCount === 1 ? '' : 's'}`;

  return `${humanizeEnum(unit.condition)} · ${rented}`;
}

/**
 * Garments with the ones already in the booking being taken counted as not free — the booking has
 * them — so a card's "free" and a page's automatic choice both move on to the rest. The same array
 * back when nothing is claimed.
 */
export function claimedOut<T extends AvailableUnit>(
  units: readonly T[],
  claimed: ReadonlySet<number>,
): readonly T[] {
  return claimed.size === 0
    ? units
    : units.map((unit) => (claimed.has(unit.unitId) ? { ...unit, free: false } : unit));
}

/** Everything a design page shows for one choice of look and size. */
export interface DesignPage {
  looks: DesignOption[];
  /** The look in force: the one chosen while the design still has it, otherwise the first. */
  look: string | undefined;
  /** False for a design with one look and nothing to name it by — a screen draws no colour row. */
  showLooks: boolean;
  sizes: DesignOption[];
  /** The size in force: the one chosen while the look has it, otherwise the first free one. */
  size: string | undefined;
  /** The garments of that look and size, free or not. */
  garments: AvailableUnit[];
  /** What booking by look and size would set aside — {@link pickGarment}. */
  setAside: AvailableUnit | null;
  /**
   * What an Add would book: the garment the clerk chose instead, while it is still free and of this
   * look and size, otherwise {@link setAside}.
   */
  pick: AvailableUnit | null;
  /** True when {@link pick} is the clerk's choice rather than the automatic one. */
  overridden: boolean;
  /** The look's own picture (or a sibling's), else the design's, else null for the outline. */
  picture: string | null;
  /** The garment the price is quoted from: the one set aside, else the first of the size. */
  priced: AvailableUnit | null;
  charge: SlabCharge | null;
  /** Its card as a shop quotes it, in the hang tag's words. Empty when nothing prices it. */
  ladder: TagRateLine[];
}

/**
 * A design page for one choice — the website's popup and the phone's design screen both read this,
 * so the size a screen falls back to, the garment it sets aside and the price it quotes cannot
 * differ between them.
 *
 * <p>
 * <strong>The fallbacks are derived, never stored.</strong> A size picked for the maroon need not
 * exist in the gold, and the refetch that keeps availability honest must never take a clerk's
 * choice away; so the screen keeps what was tapped and this decides what is in force.
 * </p>
 */
export function designPage(
  design: Pick<CatalogueDesign, 'units' | 'imageUrl'>,
  days: number,
  chosen: {
    look?: string | undefined;
    size?: string | undefined;
    /** A garment the clerk tapped in place of the automatic choice. */
    garment?: number | undefined;
  },
  currency: string,
  /**
   * Garments already in the booking being taken. They are counted as not free — the booking has
   * them — so the chips and the automatic choice move on to the next one.
   */
  claimed: ReadonlySet<number> = new Set(),
): DesignPage {
  const units = claimedOut(design.units, claimed);
  const looks = lookOptions(units, days);
  const look = looks.some((option) => option.key === chosen.look) ? chosen.look : looks[0]?.key;
  const sizes = sizeOptions(units, look, days);
  const size = sizes.some((option) => option.key === chosen.size)
    ? chosen.size
    : (sizes.find((option) => option.freeCount > 0)?.key ?? sizes[0]?.key);

  const ofLook = units.filter((unit) => look === undefined || lookKey(unit) === look);
  const garments = ofLook.filter((unit) => size === undefined || sizeKey(unit) === size);
  const setAside = pickGarment(units, { look, size });
  const chosenGarment = garments.find((unit) => unit.unitId === chosen.garment && unit.free);
  const pick = chosenGarment ?? setAside;
  const priced = pick ?? garments[0] ?? null;
  const rate = priced
    ? rateOf(
        {
          bands: priced.rateCard.bands.map((band) => ({
            ...band,
            perExtraDay: band.perExtraDay ?? null,
          })),
          adjustmentPct: priced.rateCard.adjustmentPct,
          currencySymbol: currency,
        },
        Number.POSITIVE_INFINITY,
      )
    : null;

  return {
    looks,
    look,
    showLooks: looks.length > 1 || (looks[0] !== undefined && looks[0].label !== ''),
    sizes,
    size,
    garments,
    setAside,
    pick,
    overridden: chosenGarment !== undefined && chosenGarment !== setAside,
    picture: ofLook.find((unit) => unit.imageUrl)?.imageUrl ?? design.imageUrl,
    priced,
    charge: priced ? priceGarment(priced.rateCard, days) : null,
    ladder: rate?.kind === 'lines' ? rate.lines : [],
  };
}

/**
 * The booking rail's designs narrowed to one category and one search box — `visibleGarments` for
 * design cards. A design matches on its name or code, or on any garment's code, so a code typed the
 * short way ("bjb36-1") still finds the card it belongs to — and so does the shop's own number typed
 * whole ("7", "007" or "RN-7"), as `visibleGarments` reads it. A design with nothing free is never
 * filtered out for that: "back on the 24th" is an answer a counter gives.
 */
export function visibleDesigns<
  T extends Pick<CatalogueDesign, 'itemName' | 'itemCode' | 'categoryId' | 'units'>,
>(designs: readonly T[], { category, query }: { category: number | null; query: string }): T[] {
  const needle = query.trim().toLowerCase();
  const reference = normalizeReference(query);

  return designs.filter((design) => {
    if (category !== null && design.categoryId !== category) {
      return false;
    }

    if (!needle) {
      return true;
    }

    return (
      `${design.itemName} ${design.itemCode}`.toLowerCase().includes(needle) ||
      design.units.some(
        (unit) =>
          unit.serialCode.toLowerCase().includes(needle) ||
          matchesGarmentCode(unit.serialCode, needle) ||
          (!!unit.referenceNumber && unit.referenceNumber === reference),
      )
    );
  });
}
